import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import ContactModal from './ContactModal';

/**
 * Pricing section (component name kept as SocialProof for routing/anchor
 * compatibility). Three tier descriptions with "Contact us for pricing" CTA.
 * Honest framing: no fabricated logos, testimonials, or metrics.
 */
const SocialProof = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTier, setActiveTier] = useState(null);

  const tiers = [
    {
      name: 'Starter',
      audience: 'Early-stage HR teams preparing for the directive.',
      employees: 'Up to 250 employees',
      features: [
        'Article 9 reporting',
        'Article 7 employee disclosures',
        '5 EU languages',
        'EU-hosted infrastructure',
      ],
    },
    {
      name: 'Growth',
      audience: 'Mid-market enterprises building pay transparency programs.',
      employees: '250 to 1,000 employees',
      features: [
        'Everything in Starter',
        'Article 10 joint pay assessments',
        'Two-person approval workflows',
        'Multi-tenant white-label',
        'Priority support',
      ],
      highlighted: true,
    },
    {
      name: 'Enterprise',
      audience: 'Large EU organizations subject to full directive obligations.',
      employees: '1,000+ employees',
      features: [
        'Everything in Growth',
        'Custom HCM integrations',
        'Single sign-on (SSO)',
        'Dedicated success manager',
        'Audit-ready evidence packs',
      ],
    },
  ];

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
            <Icon name="Tag" size={16} />
            <span>Pricing</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
            Built for every stage of EU compliance
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Transparent tiers, scoped to your organization. No public price list,
            no surprises. Talk to us and we'll quote based on headcount, modules,
            and integration scope.
          </p>
        </div>

        {/* Tier Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`bg-white rounded-2xl p-8 flex flex-col ${
                tier.highlighted
                  ? 'shadow-lg border-2 border-primary'
                  : 'shadow-soft border border-border'
              }`}
            >
              {tier.highlighted && (
                <div className="inline-block self-start mb-3 px-3 py-1 bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wide rounded-full">
                  Most popular
                </div>
              )}
              <h3 className="text-2xl font-bold text-foreground mb-2">{tier.name}</h3>
              <p className="text-sm text-muted-foreground mb-1">{tier.audience}</p>
              <p className="text-xs text-muted-foreground font-medium mb-6">{tier.employees}</p>

              <div className="border-t border-border pt-6 mb-6">
                <div className="text-foreground font-semibold mb-1">Contact us for pricing</div>
                <div className="text-xs text-muted-foreground">
                  Quoted per organization
                </div>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => setActiveTier(tier.name)}
                className={`w-full py-3 rounded-lg font-semibold transition-smooth ${
                  tier.highlighted
                    ? 'bg-accent text-white hover:bg-accent/90 shadow-sm'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                Contact Us
              </button>
            </div>
          ))}
        </div>

        {/* Trust line */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground max-w-3xl mx-auto">
            All plans include EU-hosted infrastructure, native support for 5 EU languages
            (English, German, French, Italian, Spanish), and full Article 9, 10, and 7
            compliance under EU Directive 2023/970.
          </p>
        </div>
      </div>

      <ContactModal
        isOpen={activeTier !== null}
        tier={activeTier || ''}
        onClose={() => setActiveTier(null)}
      />
    </section>
  );
};

export default SocialProof;
